import { useEffect, useState } from "react";
import SearchWithDropdown from "../../form/SearchWithDropdown";
import EventItem from "../../items/EventItem";
import Button from "../../form/Button";
import SortDropdown from "../../form/SortDropdown";
import { eventList } from "../../../services/eventService";
import { toast } from "react-toastify";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import EventFiltersModal from "./EventFiltersModal";
import { lngDetect } from "../../../helpers/lngDetect";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import EventItemLoader from "../../loader/EventItemLoader";
import NoData from "../../elements/Nodata";
import noDataImage from "../../../assets/images/tobaco-no.svg";
import { useTranslation } from "react-i18next";

const EventSection = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { lang } = useSelector((state) => state?.langProvider);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Separate states for typing and selected location
  const [locationKeyword, setLocationKeyword] = useState(""); // For typing in search box
  const [selectedLocation, setSelectedLocation] = useState(""); // For actual selected location

  const searchLocation = useDebounce(locationKeyword, 300); // For dropdown API
  const search = selectedLocation; // This will be used for the main event search

  const [filters, setFilters] = useState({
    limit: 8,
    page: 1,
    sortQuery: "-createdAt",
  });

  const [dropdownData, setDropdownData] = useState();

  // This effect handles the location dropdown search (OpenStreetMap API)
  useEffect(() => {
    if (searchLocation?.length < 3) {
      setDropdownData([]);
      return;
    }

    const controller = new AbortController();
    const delayDebounce = setTimeout(() => {
      fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${searchLocation}`,
        { signal: controller.signal }
      )
        .then((res) => res.json())
        .then((data) => {
          setDropdownData(
            data?.map((item, index) => ({
              _id: index,
              name: item?.display_name,
              sendName: item?.name,
              type: "list",
            }))
          );
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error(err);
        });
    }, 300);

    return () => {
      clearTimeout(delayDebounce);
      controller.abort();
    };
  }, [searchLocation]); // Changed from keyword to searchLocation

  // Handle sort functionality
  const handleSort = (sortType) => {
    let sortQuery = "-createdAt"; // default

    switch (sortType) {
      case "Default":
        sortQuery = "-createdAt";
        break;
      case "Dates":
        sortQuery = "startDate";
        break;
      case "Dates Desc":
        sortQuery = "-startDate";
        break;
      case "Title A-Z":
        sortQuery = "title";
        break;
      case "Title Z-A":
        sortQuery = "-title";
        break;
      case "Price Low-High":
        sortQuery = "minTicketPrice";
        break;
      case "Price High-Low":
        sortQuery = "-minTicketPrice";
        break;
      default:
        sortQuery = "-createdAt";
    }

    setFilters((prev) => ({
      ...prev,
      sortQuery,
      page: 1,
    }));
  };

  const sortOptions = [
    {
      label: "Default",
      icon: "fa-solid fa-arrow-down-wide-short",
      click: () => handleSort("Default"),
    },
    {
      label: "Dates (Oldest First)",
      icon: "fa-solid fa-arrow-up",
      click: () => handleSort("Dates"),
    },
    {
      label: "Dates (Latest First)",
      icon: "fa-solid fa-arrow-down",
      click: () => handleSort("Dates Desc"),
    },
    // {
    //   label: "Title (A-Z)",
    //   icon: "fa-solid fa-arrow-up",
    //   click: () => handleSort("Title A-Z"),
    // },
    // {
    //   label: "Title (Z-A)",
    //   icon: "fa-solid fa-arrow-down",
    //   click: () => handleSort("Title Z-A"),
    // },
    {
      label: "Price (Low to High)",
      icon: "fa-solid fa-arrow-up",
      click: () => handleSort("Price Low-High"),
    },
    {
      label: "Price (High to Low)",
      icon: "fa-solid fa-arrow-down",
      click: () => handleSort("Price High-Low"),
    },
  ];

  const {
    data: eventsData,
    isLoading: eventLoading,
    isFetching: eventFetching,
  } = useQuery({
    queryKey: ["Event-Front", filters, search],
    queryFn: async () => {
      const filter_data = { ...filters, ...filters?.filterBy };
      delete filter_data?.filterBy;
      const res = await eventList({
        ...filter_data,
        published: true,
        ...(search && { address: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      if (res?.docs?.status === 200) {
        const new_data_arr = res?.docs?.data?.map((item) => {
          const date = `${
            item?.startDate
              ? dayjs(item?.startDate).format(
                  item?.endDate ? "DD" : "DD MMM YYYY"
                )
              : ""
          }${
            item?.endDate
              ? " - " + dayjs(item?.endDate).format("DD MMM YYYY")
              : ""
          }`;
          return {
            ...item,
            id: item?._id,
            eventTitle: item?.title,
            eventDate: date || "",
            eventLocation: item?.address?.name,
            eventMinRate: item?.minTicketPrice || false,
            images: item?.gallery?.map((img) => img?.url) || [],
            tags: item?.tagData?.map((item) => item?.name) || [],
            link: `/event-details/${item?.uid}`,
          };
        });

        if (search || filters.page === 1) {
          return {
            ...res?.docs,
            data: [...new_data_arr],
          };
        } else {
          return {
            ...res?.docs,
            data: [...(eventsData?.data || []), ...new_data_arr]?.filter(
              (item, index, self) =>
                index === self.findIndex((t) => t?._id === item?._id)
            ),
          };
        }
      } else {
        return {
          ...res,
          data: [],
        };
      }
    },
    ...(!filters?.filterBy && {
      placeholderData: keepPreviousData,
    }),
  });

  const loadMore = () => {
    if (eventsData?.metadata?.hasNextPage) {
      setFilters((pre) => ({
        ...pre,
        page: eventsData?.metadata?.nextPage,
      }));
    }
  };

  const handleSelect = (place) => {
    if (place) {
      // Set both the display value and trigger the search
      setLocationKeyword(place?.name);
      setSelectedLocation(place?.sendName); // This triggers the event search
    } else {
      // Clear both states
      setLocationKeyword("");
      setSelectedLocation("");
    }
    setDropdownData([]);
  };

  function addToFavoriteResponse(res) {
    if (res?.status === 200) {
      queryClient.setQueryData(["Event-Front", filters, search], (state) => {
        const state_data = JSON.parse(JSON.stringify(state || {}));
        const new_arr = state_data?.data?.map((item) =>
          item?._id === res?.data?._id ? { ...item, ...res?.data } : item
        );
        return { ...state_data, data: new_arr };
      });
    }
  }

  const getCurrentSortLabel = () => {
    const currentSort = filters.sortQuery;
    switch (currentSort) {
      case "-createdAt":
        return "Default";
      case "startDate":
        return "Dates (Earliest First)";
      case "-startDate":
        return "Dates (Latest First)";
      case "title":
        return "Title (A-Z)";
      case "-title":
        return "Title (Z-A)";
      case "minTicketPrice":
        return "Price (Low to High)";
      case "-minTicketPrice":
        return "Price (High to Low)";
      default:
        return "Default";
    }
  };

  return (
    <>
      <section className="relative py-20 md:py-24 bg-magnolia px-5 xl:px-auto">
        <div className="max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl w-full mx-auto relative space-y-10">
          <div className="flex flex-wrap items-center justify-between gap-3 md:gap-auto">
            <h2 className="text-2xl lg:text-3xl xl:text-5xl 2xl:text-6xl text-deep-purple uppercase font-bold flex-shrink-0">
              {t("DANCE EVENTS")}
            </h2>
            <div className="relative w-full flex-shrink max-w-sm 2xl:max-w-xl">
              <SearchWithDropdown
                placeholder={t("Choose your location")}
                dropdownData={dropdownData}
                keyword={locationKeyword} // Changed from keyword to locationKeyword
                setKeyword={setLocationKeyword} // Changed from setKeyword to setLocationKeyword
                onChange={(selected) => handleSelect(selected)}
                xPlacement="bottom"
                divClasses={"rounded-full"}
                icon={"fa-solid fa-location-dot"}
                iconClasses={"text-gradient-red"}
              />
            </div>
            <div className="flex items-center gap-5 flex-shrink-0">
              <div className="relative">
                <SortDropdown
                  label={t("Sort by")}
                  data={sortOptions}
                  buttonClasses="bg-transparent text-deep-purple"
                  xPlacement="bottomRight"
                  defaultSort="Default"
                />
              </div>
              <div className="relative">
                <Button
                  buttonLabel={t("filter")}
                  buttonLabelClasses={
                    "uppercase text-white font-semibold text-lg"
                  }
                  buttonClasses={
                    "h-12 px-10 flex items-center justify-center bg-secondary-gradient"
                  }
                  buttonFunction={() => setIsModalOpen(true)}
                />
              </div>
            </div>
          </div>

          {eventLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 2xl:gap-8">
              {Array.from({ length: 8 }, (_, i) => (
                <EventItemLoader key={i} />
              ))}
            </div>
          ) : eventsData?.data.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 2xl:gap-8">
              {eventsData?.data.map((event) => (
                <EventItem
                  key={event?.id || event?._id}
                  data={event}
                  addToFavoriteResponse={addToFavoriteResponse}
                />
              ))}
            </div>
          ) : (
            <NoData image={noDataImage} message="No events found" />
          )}
          {eventsData?.data?.length > 0 &&
            eventsData?.data?.length < eventsData?.metadata?.totalDocs && (
              <div className="flex space-y-4 items-center justify-between">
                <div className="text-deep-purple text-xl 2xl:text-2xl font-semibold text-center">
                  {t("Showing")} {eventsData?.data?.length} {t("of")}{" "}
                  {eventsData?.metadata?.totalDocs}
                </div>
                {eventsData?.metadata?.hasNextPage && (
                  <div className="flex justify-center">
                    <Button
                      buttonLabel={t("Load More")}
                      buttonLabelClasses="text-lg uppercase text-white"
                      buttonClasses="h-14 bg-secondary-gradient"
                      buttonFunction={loadMore}
                      {...(eventFetching && {
                        loading: true,
                        buttonIconPosition: "left",
                        isDisable: true,
                      })}
                    />
                  </div>
                )}
              </div>
            )}
        </div>
      </section>
      <EventFiltersModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onChange={(val) => {
          setFilters((pre) => ({
            ...pre,
            filterBy: {
              ...val?.date,
              categories: [...val?.event, ...val?.danceStyle],
            },
            page: 1,
          }));
          setIsModalOpen(false);
        }}
      />
    </>
  );
};

export default EventSection;
